import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Crop from './Crop';
import BatchUpload from './BatchUpload';
import { Warning } from './CropGuidelines';
import MetadataFields from './MetadataFields';
import Button from '../Button';
import DragAndDrop from '../DragAndDrop';
import Heading4 from '../Typography/Heading/Headline4';
import generateUpload from './generateUpload';
import acceptFiles from '../lib/acceptFiles';
import useHideScrollBar from '../lib/useHideScrollBar';

export const SubmitOverlay = styled.div`
  background-color: white;
  position: fixed;
  top: 0;
  left: 0;
  width: 98vw;
  height: 96vh;
  padding: 2vh 1vw;
  z-index: 2000;
  overflow-y: scroll;
`;

const SubmitContainer = styled.div`
  width: 100%;
  background-color: white;
`;

const ErrorsWrapper = styled.div`
  margin: 0 0 8px;
`;

const setInitialCrop = (
  image,
  aspect,
  dimensionsFixed,
  setCrop,
  setCompletedCrop,
) => {
  const centerVertical = image.width / image.height <= aspect || !aspect;

  const x = centerVertical
    ? 0
    : (image.width - (image.height * aspect)) / 2;
  const y = centerVertical
    ? (image.height - (image.width / aspect)) / 2
    : 0;

  const cropWidth = centerVertical ? image.width : image.height * aspect;
  const cropHeight = centerVertical ? image.width / aspect : image.height;

  const initialCrop = {
    width: dimensionsFixed ? cropWidth : image.width,
    height: dimensionsFixed ? cropHeight : image.height,
    x: dimensionsFixed ? x : 0,
    y: dimensionsFixed ? y : 0,
    unit: 'px',
  };

  setCrop(initialCrop);
  setCompletedCrop(initialCrop);
};

const isWithinRange = (
  testValue,
  referenceValue,
  tolerance = 1,
) => testValue >= (referenceValue - tolerance)
&& testValue <= (referenceValue + tolerance);

const ImageUpload = ({
  id,
  onSave,
  width,
  height,
  required,
  addCredit,
  imageCreditHelperText,
  addMetadata,
  submitLabel,
  sourceImage,
  sourceCredits,
  sourceTitle,
  sourceDescription,
  creditRequired,
  errors,
  hints,
  conditions,
  DragAndDropComponent,
  fileTypes,
  uploadOriginalFile,
  onSelectOriginal,
  allowBatchUpload,
}) => {
  const dimensionsFixed = Boolean(width && height);

  const aspect = dimensionsFixed ? width / height : undefined;
  const [upImg, setUpImg] = useState();
  const [fileName, setFileName] = useState();
  const imgReference = useRef(null);
  const originalImgReference = useRef(null);
  const batchUpload = Array.isArray(upImg);

  const [imageCredit, setImageCredit] = useState('');
  const [creditError, setCreditError] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [originalImageLoaded, setOriginalImageLoaded] = useState(false);
  const [cropImageLoaded, setCropImageLoaded] = useState(false);
  const [fileInputError, setFileInputError] = useState('');

  const [imageTitle, setImageTitle] = useState('');
  const [imageDescription, setImageDescription] = useState('');

  const [uploadOriginal, setUploadOriginal] = useState();

  const hasImage = Boolean(upImg);
  useEffect(() => {
    const url = sourceImage;
    if (url) {
      setUpImg(url);
      const filename = sourceImage && sourceImage.match(/[^/]+$/)[0];
      setFileName(filename);
      setImageCredit(sourceCredits);
      setImageTitle(sourceTitle);
      setImageDescription(sourceDescription);
    }
  }, [
    sourceImage,
    setImageCredit,
    sourceCredits,
    sourceTitle,
    sourceDescription,
  ]);

  const [crop, setCrop] = useState({});
  const [completedCrop, setCompletedCrop] = useState();
  const [uiScaling, setUIScaling] = useState(1);

  useEffect(() => {
    const img = document.querySelector('.ReactCrop__child-wrapper > img');
    if (img) {
      img.addEventListener('load', (event_) => {
        const image = event_.target;

        setCropImageLoaded(true);
        setDisabled(false);
        setInitialCrop(
          image,
          aspect,
          dimensionsFixed,
          setCrop,
          setCompletedCrop,
        );
      });
    }
  }, [hasImage, aspect, dimensionsFixed]);

  const onSelectFile = ({ target: { files } }) => {
    if ([...files].length > 1) {
      setUpImg([...files]);
      return;
    }

    const reader = new FileReader();
    reader.addEventListener('load', () => setUpImg(reader.result));
    const [file] = files;
    if (!fileTypes.split(', ').includes(file.type)) {
      setFileInputError(
        `Accepted file types: ${fileTypes}`,
      );
      return;
    }
    reader.readAsDataURL(file);
    setFileName(file.name);
    if (uploadOriginalFile) {
      setUploadOriginal(file);
    }
    setOriginalImageLoaded(false);
  };

  const onLoad = useCallback(({ target }) => {
    imgReference.current = target;

    return false;
  }, []);

  const onOriginalImageLoaded = useCallback(() => {
    setOriginalImageLoaded(true);

    return false;
  }, []);

  useEffect(() => {
    const img = imgReference.current;
    const originalImg = originalImgReference.current;

    if (!originalImageLoaded || !img || !originalImg) {
      return;
    }

    setUIScaling(originalImg.naturalWidth / img.width);
    setInitialCrop(
      img,
      aspect,
      dimensionsFixed,
      setCrop,
      setCompletedCrop,
    );
  }, [
    aspect,
    dimensionsFixed,
    originalImgReference,
    imgReference,
    originalImageLoaded,
  ]);

  useHideScrollBar(Boolean(upImg));

  const onSubmit = useCallback((fileData) => {
    if (Array.isArray(fileData)) {
      return onSave(
        fileData,
        imageTitle,
        imageDescription,
        imageCredit,
        setDisabled,
      );
    }
    return generateUpload(
      uploadOriginal || originalImgReference.current,
      completedCrop,
      fileName,
      onSave,
      uiScaling,
      imageTitle,
      imageDescription,
      imageCredit,
      setDisabled,
      uploadOriginalFile || Array.isArray(fileData),
    );
  },
  [
    uploadOriginal,
    uploadOriginalFile,
    originalImgReference,
    completedCrop,
    fileName,
    onSave,
    uiScaling,
    imageCredit,
    imageTitle,
    imageDescription,
    setDisabled,
  ]);

  return (
    upImg
      ? (
        <SubmitOverlay>
          <Heading4 tag="div">
            {
                  uploadOriginal
                    ? 'Image Preview'
                    : (batchUpload
                      ? undefined
                      : 'Select the desired portion of the image'
                    )
                }
          </Heading4>
          <SubmitContainer>
            { batchUpload ? (
              <BatchUpload files={upImg} />
            ) : (
              <Crop
                upImg={upImg}
                onLoad={onLoad}
                crop={crop}
                aspect={aspect}
                onChange={(newCrop) => setCrop(newCrop)}
                onComplete={(newCrop) => setCompletedCrop(newCrop)}
                ruleOfThirds={!uploadOriginalFile}
                disabled={Boolean(uploadOriginal)}
                showGuidelines={
                      cropImageLoaded && originalImageLoaded && !uploadOriginal
                    }
                width={width}
                height={height}
                uiScaling={uiScaling}
                dimensionsFixed={dimensionsFixed}
                originalImgReference={originalImgReference}
                onOriginalImageLoaded={onOriginalImageLoaded}
              />
            )}
            <div>
              <MetadataFields
                addCredit={addCredit}
                creditRequired={creditRequired}
                addMetadata={addMetadata}
                imageCredit={imageCredit}
                setImageCredit={setImageCredit}
                imageCreditHelperText={imageCreditHelperText}
                creditError={creditError}
                imageTitle={imageTitle}
                setImageTitle={setImageTitle}
                imageDescription={imageDescription}
                setImageDescription={setImageDescription}
              />
            </div>
            { hints && (
            <div>
              { hints }
            </div>
            )}
            { errors.length > 0 && (
            <ErrorsWrapper>
              { errors.map((error, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`${error}${index}`}>
                  <Warning>
                    { error }
                  </Warning>
                </div>
              )) }
            </ErrorsWrapper>
            )}
            {(crop.width > 0 || uploadOriginal || batchUpload) && (
            <div>
              <Button
                type="button"
                data-testid="crop-upload-button"
                disabled={disabled}
                onClick={() => {
                  if (addCredit && creditRequired && imageCredit === '') {
                    setCreditError('You must set a valid image credit!');
                  } else if (
                    onSelectOriginal && sourceImage
                          && isWithinRange(
                            crop.width, imgReference?.current?.width,
                          ) && isWithinRange(
                      crop.height, imgReference?.current?.height,
                    )
                  ) {
                    onSelectOriginal({
                      url: upImg,
                      credits: imageCredit,
                      title: imageTitle,
                      description: imageDescription,
                    });
                  } else {
                    onSubmit(upImg);
                  }
                }}
              >
                {submitLabel}
              </Button>
              <Button
                type="button"
                disabled={disabled}
                onClick={
                        () => {
                          setUpImg();
                          setImageCredit('');
                          setOriginalImageLoaded(false);
                          setCropImageLoaded(false);
                          setCrop({});
                          setDisabled(false);
                        }
                      }
              >
                Cancel
              </Button>
            </div>
            )}
            { conditions && (
            <div>
              { conditions }
            </div>
            )}
          </SubmitContainer>
        </SubmitOverlay>
      )
      : (
        <DragAndDropComponent
          id={id}
          onChange={onSelectFile}
          required={required}
          fileTypes={fileTypes}
          error={fileInputError}
          multiple={allowBatchUpload}
        />
      )
  );
};

ImageUpload.propTypes = {
  id: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  required: PropTypes.bool.isRequired,
  addCredit: PropTypes.bool,
  imageCreditHelperText: PropTypes.string,
  addMetadata: PropTypes.bool,
  submitLabel: PropTypes.string,
  sourceImage: PropTypes.string,
  sourceCredits: PropTypes.string,
  sourceTitle: PropTypes.string,
  sourceDescription: PropTypes.string,
  errors: PropTypes.arrayOf(
    PropTypes.string,
  ),
  hints: PropTypes.node,
  conditions: PropTypes.node,
  creditRequired: PropTypes.bool,
  DragAndDropComponent: PropTypes.elementType,
  fileTypes: PropTypes.string,
  uploadOriginalFile: PropTypes.bool,
  onSelectOriginal: PropTypes.func,
  allowBatchUpload: PropTypes.bool,
};

ImageUpload.defaultProps = {
  id: undefined,
  width: undefined,
  height: undefined,
  addCredit: false,
  imageCreditHelperText: undefined,
  addMetadata: false,
  submitLabel: 'Upload',
  sourceImage: '',
  sourceCredits: '',
  sourceTitle: '',
  sourceDescription: '',
  errors: [],
  hints: undefined,
  conditions: undefined,
  creditRequired: true,
  DragAndDropComponent: DragAndDrop,
  fileTypes: acceptFiles,
  uploadOriginalFile: false,
  onSelectOriginal: undefined,
  allowBatchUpload: false,
};

export default ImageUpload;
